import * as React from 'react'

const STORAGE_PREFIX = 'cf-tool-generating-preview:'
const THUMBNAIL_SIZE = 160

function storageKey(sessionId: string) {
  return `${STORAGE_PREFIX}${sessionId}`
}

export function rememberGeneratingPreview(sessionId: string, src: string | null | undefined) {
  if (!src) {
    return
  }
  try {
    window.sessionStorage.setItem(storageKey(sessionId), src)
  } catch {
    // Storage can be full or disabled; the loader just shows without a preview.
  }
}

// Read once and clear, so a refresh mid-generation shows the loader without the preview.
export function useGeneratingPreview(sessionId: string) {
  const [src, setSrc] = React.useState<string | null>(null)

  React.useEffect(() => {
    const key = storageKey(sessionId)
    let stored: string | null = null
    try {
      stored = window.sessionStorage.getItem(key)
      window.sessionStorage.removeItem(key)
    } catch {
      return
    }
    if (stored) {
      setSrc(stored)
    }
  }, [sessionId])

  return src
}

export async function createPreviewThumbnail(file: File) {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, THUMBNAIL_SIZE / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return canvas.toDataURL('image/webp', 0.85)
}

export function usePreviewThumbnailRef(file: File | null) {
  const thumbnail = React.useRef<string | null>(null)

  React.useEffect(() => {
    thumbnail.current = null
    if (!file) {
      return
    }
    let cancelled = false
    createPreviewThumbnail(file)
      .then(src => {
        if (!cancelled) {
          thumbnail.current = src
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [file])

  return thumbnail
}
